import React, { useCallback, useEffect, useState } from 'react';

import env from '@travel/env';
import useMemberKeepAlive from '@travel/traveler-core/hooks/useMemberKeepAlive';

import ErrorDialog from 'components/ErrorDialog/index';
import ErrorPage from 'components/ErrorPage/index';
import Layout from 'components/Layout';
import useHeaderEvents from 'hooks/useHeaderEvents';
import { refreshAuthCode } from 'store/authCode/actions';
import { getIsLoggedIn } from 'store/authCode/selectors';
import { selectIsPopupError, selectIsRedirectError } from 'store/commonError/selectors';
import { clearCommonError } from 'store/commonError/slice';
import SEO from './components/SEO';

import { pushLocation } from 'store/__router/actions';

import { useAppDispatch, useAppSelector } from 'store/hooks';

import { getSEOData } from 'store/seo/selectors';

import styles from '../style/app.module.scss';

import type { PropsWithChildren } from 'react';
type AppProps = {
  params?: {
    pageIdentifier?: string;
  };
  query?: Record<string, string>;
};

const App = (props: PropsWithChildren<AppProps>) => {
  const { children } = props;
  const dispatch = useAppDispatch();

  const isRedirectError = useAppSelector(selectIsRedirectError);
  const isPopupError = useAppSelector(selectIsPopupError);

  const [shouldShowErrorDialog, setShouldShowErrorDialog] = useState(false);

  const onCloseErrorDialog = () => {
    dispatch(clearCommonError());
    setShouldShowErrorDialog(false);
  };

  useEffect(() => {
    setShouldShowErrorDialog(isPopupError);
  }, [isPopupError, setShouldShowErrorDialog]);

  const isLoggedIn = useAppSelector(getIsLoggedIn);
  const seoData = useAppSelector(getSEOData);
  // refresh token
  const refreshTokenHookCallback = useCallback(() => {
    dispatch(refreshAuthCode());
  }, [dispatch]);

  useHeaderEvents();
  useMemberKeepAlive(isLoggedIn, refreshTokenHookCallback);

  const envName = env('UNIVERSAL_ENV_NAME');

  let content = children;

  if (isRedirectError) {
    content = <ErrorPage />;
  }

  return (
    <div id="app" data-testid="app-wrapper" className={styles.appWrapper}>
      <SEO isDefaultTags={true} {...seoData} />
      {envName && (
        <button
          data-testid="app-env-button"
          className={styles.envButton}
          tabIndex={-1}
          onClick={() => {
            dispatch(
              pushLocation(
                window.location.pathname.replace(env('UNIVERSAL_BASE_NAME') as string, '') +
                  window.location.search,
              ),
            );
          }}
        >
          {envName}
        </button>
      )}
      <Layout className={styles.layout}>{content}</Layout>
      <ErrorDialog isOpen={shouldShowErrorDialog} onClose={onCloseErrorDialog} />
      <div className={styles.footer} dangerouslySetInnerHTML={{ __html: '<global-footer />' }} />
    </div>
  );
};

export default App;
